import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { workspace } from '@lawbee/domain';
import { Alert, Button, ErrorState, Field, FormError, LoadingState, Select, cn } from '@lawbee/ui';
import { Download, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { useApiMutation } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { SettingsResponse, WorkspaceSettings } from '../../lib/types';
import { ReadOnlyNote, Switch } from '../app/ops/shared';
import { SETTINGS_KEY, useWorkspaceSettings } from './firm';
import { SettingsCard } from './layout';

const UPDATE_MODES: { value: WorkspaceSettings['updateMode']; label: string; description: string }[] = [
  { value: 'approve', label: 'Lawyer approves every client update', description: 'Nothing goes to a client until a lawyer has read and approved it.' },
  { value: 'auto', label: 'Send stage updates automatically (AI drafts still need approval)', description: 'Template updates go out when a matter changes stage. Anything drafted by AI waits for approval.' },
];

export function PrivacySettingsPage() {
  const q = useWorkspaceSettings();
  if (q.isLoading) return <LoadingState label="Loading privacy settings" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return <PrivacyForm data={q.data} />;
}

function PrivacyForm({ data }: { data: SettingsResponse }) {
  const me = useMe();
  const qc = useQueryClient();
  const canEdit = can(me, 'settings');
  const isOwner = me.role === 'owner';
  const [dataRegion, setDataRegion] = useState<WorkspaceSettings['dataRegion']>(data.settings.dataRegion);
  const [aiEnabled, setAiEnabled] = useState(data.settings.aiEnabled);
  const [updateMode, setUpdateMode] = useState<WorkspaceSettings['updateMode']>(data.settings.updateMode);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDataRegion(data.settings.dataRegion);
    setAiEnabled(data.settings.aiEnabled);
    setUpdateMode(data.settings.updateMode);
  }, [data.settings]);

  const dirty = dataRegion !== data.settings.dataRegion || aiEnabled !== data.settings.aiEnabled || updateMode !== data.settings.updateMode;

  const save = useApiMutation((settings: Partial<WorkspaceSettings>) => api.patch<SettingsResponse>('/api/workspace/settings', { settings }), {
    success: 'Privacy settings saved',
    invalidate: [SETTINGS_KEY],
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    save.mutate({ dataRegion, aiEnabled, updateMode }, { onError: (err) => setError(errorMessage(err)) });
  };

  return (
    <div className="space-y-6">
      <Alert variant="success" title="Client data is never used to train AI models">
        Not by LawBee, and not by the AI provider. Every access to client data is recorded in the audit log.
      </Alert>
      <form onSubmit={submit} noValidate>
        <SettingsCard
          title="Privacy and AI"
          footer={
            canEdit ? (
              <Button type="submit" disabled={!dirty} loading={save.isPending}>
                Save changes
              </Button>
            ) : undefined
          }
        >
          <fieldset disabled={!canEdit} className="space-y-6">
            {!canEdit ? <ReadOnlyNote /> : null}
            <Field label="Data region" htmlFor="pv-region" description={workspace.DATA_REGION_NOTES[dataRegion]}>
              <Select
                id="pv-region"
                className="sm:max-w-xs"
                value={dataRegion}
                onChange={(e) => setDataRegion(e.target.value as WorkspaceSettings['dataRegion'])}
                options={[
                  { value: 'ca', label: 'Canada' },
                  { value: 'us', label: 'United States' },
                ]}
              />
            </Field>
            <div className="border-t border-border/70 pt-5">
              <Switch checked={aiEnabled} onCheckedChange={setAiEnabled} disabled={!canEdit} label="AI features" description={workspace.AI_PRIVACY_NOTE} />
            </div>
            <fieldset className="space-y-3 border-t border-border/70 pt-5">
              <legend className="mb-3 text-sm font-medium">Client updates</legend>
              {UPDATE_MODES.map((m) => (
                <label
                  key={m.value}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-xl p-3.5 transition-[box-shadow,background-color]',
                    updateMode === m.value ? 'bg-accent shadow-[inset_0_0_0_2px_var(--color-primary)]' : 'shadow-[inset_0_0_0_1px_var(--color-border)] hover:bg-muted',
                    !canEdit && 'cursor-not-allowed opacity-70',
                  )}
                >
                  <input
                    type="radio"
                    name="updateMode"
                    value={m.value}
                    checked={updateMode === m.value}
                    onChange={() => setUpdateMode(m.value)}
                    className="mt-0.5 size-4 accent-[var(--color-primary)]"
                  />
                  <span className="space-y-0.5">
                    <span className="block text-sm font-medium">{m.label}</span>
                    <span className="block text-xs text-muted-foreground">{m.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <FormError message={error} />
          </fieldset>
        </SettingsCard>
      </form>

      <SettingsCard title="Your data" description="Records of who accessed client data, and a full copy of everything your firm has stored.">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          {can(me, 'audit') ? (
            <Button asChild variant="outline">
              <Link to="/settings/audit">
                <ShieldCheck /> View audit log
              </Link>
            </Button>
          ) : null}
          {isOwner ? (
            <Button asChild variant="outline">
              <a href={apiUrl('/api/workspace/export')} download>
                <Download /> Export all data
              </a>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">Only an owner can export firm data.</p>
          )}
        </div>
      </SettingsCard>
    </div>
  );
}
